import { useEffect, useState } from "react";
import { Users, Plus, Edit2, Trash2, X, Building2, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Customers() {
  const { canEditAll, isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/customers", { params: q ? { search: q } : {} });
      setRows(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (row) => { setEditing(row); setShowForm(true); };

  const del = async (row) => {
    if (!window.confirm(`Delete customer "${row.name}"?`)) return;
    try {
      await api.delete(`/customers/${row.customer_id}`);
      toast.success("Customer deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="customers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Users size={22} className="text-orange-600" /> Customers <span className="text-base font-medium text-slate-400">({rows.length})</span></h1>
          <p className="text-sm text-slate-500 mt-0.5">OEM customers for NPD quotations.</p>
        </div>
        {canEditAll() && (
          <button onClick={openNew} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md px-4 py-2" data-testid="customer-new">
            <Plus size={16} /> New Customer
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="p-4 border-b border-slate-200">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…"
                   className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No customers found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="customers-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-3 font-semibold w-10">#</th>
                <th className="text-left p-3 font-semibold">Name</th>
                <th className="text-left p-3 font-semibold">Short Code</th>
                <th className="text-left p-3 font-semibold">Contact</th>
                <th className="text-left p-3 font-semibold">Location</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, idx) => (
                <tr key={r.customer_id} className="hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs text-slate-400">{idx + 1}</td>
                  <td className="p-3">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      <Building2 size={14} className="text-slate-400" />
                      {r.name}
                    </div>
                  </td>
                  <td className="p-3 text-slate-600">{r.short_code || "—"}</td>
                  <td className="p-3 text-slate-600">
                    {r.contact_person ? <div>{r.contact_person}</div> : null}
                    {r.contact_email ? <div className="text-xs text-slate-400">{r.contact_email}</div> : null}
                    {!r.contact_person && !r.contact_email ? "—" : null}
                  </td>
                  <td className="p-3 text-slate-600">{r.location || "—"}</td>
                  <td className="p-3 text-right">
                    {canEditAll() && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-700"><Edit2 size={14} /></button>
                        {isAdmin() && <button onClick={() => del(r)} className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {showForm && (
        <CustomerForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function CustomerForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial || { name: "", short_code: "", contact_person: "", contact_email: "", location: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (initial?.customer_id) await api.put(`/customers/${initial.customer_id}`, f);
      else await api.post("/customers", f);
      toast.success(initial ? "Customer updated" : "Customer created");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-lg w-full max-w-lg shadow-xl" data-testid="customer-form">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="font-semibold text-slate-900">{initial ? "Edit Customer" : "New Customer"}</h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Name *"><input required className="input" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="customer-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Short Code"><input className="input" value={f.short_code || ""} onChange={(e) => set("short_code", e.target.value)} /></Field>
            <Field label="Location *"><input required className="input" value={f.location || ""} onChange={(e) => set("location", e.target.value)} /></Field>
          </div>
          <Field label="Contact Person *"><input required className="input" value={f.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} /></Field>
          <Field label="Contact Email *"><input required type="email" className="input" value={f.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} /></Field>
          <Field label="Notes"><textarea rows={2} className="input" value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
        <div className="p-5 border-t flex justify-end gap-2 bg-slate-50 rounded-b-lg">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-semibold rounded-md" data-testid="customer-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
